import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Folder, File, Download, Eye, Calendar, FileText, 
  Image, Video, Music, Archive, RefreshCw, Home, ChevronRight, 
  AlertCircle, Grid, List, X, ChevronLeft, Play, Pause,
  ZoomIn, Maximize2, Upload, CloudUpload, BarChart3, FolderOpen, TrendingUp, Package, ImageIcon, Wallet, Target, FileSignature,
  MessageCircle, Palette, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClientReports } from './ClientReports';
import { UpcomingSessions } from './UpcomingSessions';
import { ProgressTab } from './ProgressTab';
import DeliverablesTab from './DeliverablesTab';
import ContentReviewTab from './ContentReviewTab';
import MyAccountTab from './MyAccountTab';
import ObjectivesPortalTab from './ObjectivesPortalTab';
import ContractsPortalTab from './ContractsPortalTab';
import BrandAssetsTab from './BrandAssetsTab';
import ApprovedGalleryTab from './ApprovedGalleryTab';
import { SocialMediaLinks } from './SocialMediaLinks';
import ClientPackageSummary from './ClientPackageSummary';
import { clearSession } from '../../services/sessionService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lightbox/Presentation Modal Component
function MediaLightbox({ files, currentIndex, onClose, onNext, onPrev }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const currentFile = files[currentIndex];
  
  // Auto-advance slideshow
  useEffect(() => {
    if (isPlaying) {
      const timer = setTimeout(() => {
        if (currentIndex < files.length - 1) {
          onNext();
        } else {
          setIsPlaying(false);
        }
      }, 4000); // 4 seconds per slide
      return () => clearTimeout(timer);
    }
  }, [isPlaying, currentIndex, files.length, onNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(!isPlaying);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev, isPlaying]);

  const isImage = currentFile?.mimeType?.startsWith('image/');
  const isVideo = currentFile?.mimeType?.startsWith('video/');

  // Get high-res image URL - use thumbnail with larger size
  const getImageUrl = (file) => {
    if (file.thumbnailLink) {
      // Replace thumbnail size with maximum size (s0 = original size)
      return file.thumbnailLink.replace(/=s\d+$/, '=s1200');
    }
    // Fallback to direct Drive URL
    if (file.id) {
      return `https://drive.google.com/thumbnail?id=${file.id}&sz=w1200`;
    }
    return null;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex flex-col"
      onClick={onClose}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-[var(--ios-text-primary)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[var(--ios-text-tertiary)]">
            {currentIndex + 1} de {files.length}
          </span>
          <h3 className="font-semibold truncate max-w-md">{currentFile?.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title={isPlaying ? 'Pausar' : 'Reproducir presentación'}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          {currentFile?.webContentLink && (
            <a
              href={currentFile.webContentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Descargar"
            >
              <Download size={20} />
            </a>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center relative px-16" onClick={e => e.stopPropagation()}>
        {/* Previous button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          disabled={currentIndex === 0}
          className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={32} className="text-[var(--ios-text-primary)]" />
        </button>

        {/* Media content */}
        <div className="max-w-5xl max-h-[80vh] flex items-center justify-center">
          {isImage && (
            <img
              src={getImageUrl(currentFile)}
              alt={currentFile.name}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
          )}
          {isVideo && (
            <div className="w-full max-w-4xl aspect-video rounded-lg overflow-hidden shadow-2xl bg-black">
              <iframe
                src={`https://drive.google.com/file/d/${currentFile.id}/preview`}
                width="100%"
                height="100%"
                allow="autoplay; encrypted-media"
                allowFullScreen
                className="border-0"
                title={currentFile.name}
              />
            </div>
          )}
          {!isImage && !isVideo && (
            <div className="text-center text-[var(--ios-text-primary)]">
              <File size={80} className="mx-auto mb-4 text-[var(--ios-text-tertiary)]" />
              <p className="text-xl font-semibold mb-2">{currentFile?.name}</p>
              <a
                href={currentFile?.webViewLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg transition-colors"
              >
                <Eye size={20} />
                Ver en Google Drive
              </a>
            </div>
          )}
        </div>

        {/* Next button */}
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          disabled={currentIndex === files.length - 1}
          className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={32} className="text-[var(--ios-text-primary)]" />
        </button>
      </div>

      {/* Thumbnails strip */}
      <div className="p-4 overflow-x-auto" onClick={e => e.stopPropagation()}>
        <div className="flex gap-2 justify-center">
          {files.map((file, idx) => (
            <button
              key={file.id}
              onClick={() => {
                const diff = idx - currentIndex;
                if (diff > 0) for (let i = 0; i < diff; i++) onNext();
                if (diff < 0) for (let i = 0; i < Math.abs(diff); i++) onPrev();
              }}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              {file.thumbnailLink ? (
                <img src={file.thumbnailLink} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                  <File size={20} className="text-[var(--ios-text-tertiary)]" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="text-center text-[var(--ios-text-tertiary)] text-xs pb-4">
        Usa ← → para navegar • Espacio para presentación automática • Esc para cerrar
      </div>
    </div>
  );
}

export const PortalDashboard = () => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderHistory, setFolderHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' or 'list'
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeTab, setActiveTab] = useState('progress'); // 'progress', 'files', 'reports', or 'sessions'
  const [isAdminPreview, setIsAdminPreview] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for admin preview token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const previewToken = urlParams.get('preview_token');
    const clientName = urlParams.get('client_name');
    
    if (previewToken) {
      // Admin preview mode - use the preview token
      localStorage.setItem('portal_token', previewToken);
      setIsAdminPreview(true);
      
      // Fetch client info using the preview token
      const fetchPreviewClient = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${previewToken}` }
          });
          
          const clientData = response.data.client;
          setUser({
            name: clientName || clientData.name,
            email: clientData.email,
            client_id: clientData.client_id
          });
          
          // Fetch businesses for this client
          const bizResponse = await axios.get(`${API_URL}/api/clients/${clientData.client_id}/businesses`, {
            headers: { Authorization: `Bearer ${previewToken}` }
          });
          
          if (bizResponse.data && bizResponse.data.length > 0) {
            const firstBusiness = bizResponse.data[0];
            setBusiness(firstBusiness);
            localStorage.setItem('selected_business', JSON.stringify(firstBusiness));
            loadFolderContent(null, firstBusiness.name || 'Mi Carpeta', firstBusiness);
          } else {
            setLoading(false);
            setError('Este cliente no tiene negocios configurados');
          }
        } catch (err) {
          console.error('Error loading preview:', err);
          setError('Error al cargar la vista previa');
          setLoading(false);
        }
      };
      
      fetchPreviewClient();
      return;
    }
    
    // Normal flow - check for existing token
    const token = localStorage.getItem('portal_token');
    const userData = localStorage.getItem('portal_user');
    const selectedBusiness = localStorage.getItem('selected_business');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if a business is selected
      if (selectedBusiness) {
        const parsedBusiness = JSON.parse(selectedBusiness);
        setBusiness(parsedBusiness);
        loadFolderContent(null, parsedBusiness.name || 'Mi Carpeta', parsedBusiness);
      } else {
        // Try to fetch and auto-select the first business for this client
        const fetchAndSelectBusiness = async () => {
          try {
            const clientId = parsedUser.client_id || parsedUser.user_id;
            const response = await axios.get(`${API_URL}/api/client-portal/my-businesses`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.businesses && response.data.businesses.length > 0) {
              const firstBusiness = response.data.businesses[0];
              localStorage.setItem('selected_business', JSON.stringify(firstBusiness));
              setBusiness(firstBusiness);
              loadFolderContent(null, firstBusiness.business_name || firstBusiness.name || 'Mi Carpeta', firstBusiness);
            } else {
              // No businesses - redirect to selector
              navigate('/portal/select-business');
            }
          } catch (err) {
            console.error('Error fetching businesses:', err);
            navigate('/portal/select-business');
          }
        };
        
        fetchAndSelectBusiness();
      }
    } catch (e) {
      navigate('/login');
    }
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('portal_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleChangeBusiness = () => {
    localStorage.removeItem('selected_business');
    navigate('/portal/select-business');
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    
    setUploading(true);
    setShowUploadModal(false);
    
    try {
      const formData = new FormData();
      
      // Add all files to form data
      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append('files', selectedFiles[i]);
      }
      
      const response = await axios.post(
        `${API_URL}/api/upload/files`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.uploaded && response.data.uploaded.length > 0) {
        toast.success(`${response.data.uploaded.length} archivo(s) subido(s) correctamente`);
        // Reload folder content to show new files
        loadFolderContent(currentFolder?.id || null, currentFolder?.name || 'Mi Carpeta');
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} archivo(s) fallaron al subir`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Error al subir archivos');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadFolderContent = async (folderId, folderName, currentBusiness = business) => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      
      // Use business-specific folder if available
      const businessFolderId = currentBusiness?.drive_folder_id;
      const targetFolderId = folderId || businessFolderId;
      
      const filesUrl = targetFolderId 
        ? `${API_URL}/api/drive/files?folder_id=${targetFolderId}`
        : `${API_URL}/api/drive/files`;
      
      const filesResponse = await axios.get(filesUrl, { headers });
      setFiles(filesResponse.data.files || []);
      
      const foldersUrl = targetFolderId
        ? `${API_URL}/api/drive/folders?folder_id=${targetFolderId}`
        : `${API_URL}/api/drive/folders`;
      
      const foldersResponse = await axios.get(foldersUrl, { headers });
      setFolders(foldersResponse.data.folders || []);
      
      if (folderId) {
        setCurrentFolder({ id: folderId, name: folderName });
      } else {
        setCurrentFolder(null);
      }
      
    } catch (err) {
      console.error('Error loading folder:', err);
      const errorMsg = err.response?.data?.detail || 'Error al cargar archivos';
      setError(errorMsg);
      
      if (errorMsg.includes('not connected') || errorMsg.includes('no está conectado')) {
        toast.error('Google Drive no está conectado. Contacta al administrador.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folderId, folderName) => {
    if (currentFolder) {
      setFolderHistory([...folderHistory, currentFolder]);
    }
    loadFolderContent(folderId, folderName);
  };

  const navigateBack = () => {
    if (folderHistory.length > 0) {
      const newHistory = [...folderHistory];
      const previousFolder = newHistory.pop();
      setFolderHistory(newHistory);
      loadFolderContent(previousFolder?.id || null, previousFolder?.name || 'Mi Carpeta');
    } else {
      loadFolderContent(null, 'Mi Carpeta');
    }
  };

  const navigateToRoot = () => {
    setFolderHistory([]);
    loadFolderContent(null, 'Mi Carpeta');
  };

  const handleLogout = async () => {
    await clearSession();
    navigate('/portal');
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File className="text-[var(--ios-text-tertiary)]" size={24} />;
    if (mimeType.startsWith('image/')) return <Image className="text-blue-500" size={24} />;
    if (mimeType.startsWith('video/')) return <Video className="text-purple-500" size={24} />;
    if (mimeType.startsWith('audio/')) return <Music className="text-pink-500" size={24} />;
    if (mimeType.includes('pdf')) return <FileText className="text-red-500" size={24} />;
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <Archive className="text-yellow-500" size={24} />;
    return <File className="text-[var(--ios-text-tertiary)]" size={24} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const num = parseInt(bytes);
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Separate media files (images/videos) from other files
  const mediaFiles = files.filter(f => 
    f.mimeType?.startsWith('image/') || f.mimeType?.startsWith('video/')
  );
  const otherFiles = files.filter(f => 
    !f.mimeType?.startsWith('image/') && !f.mimeType?.startsWith('video/')
  );

  const openLightbox = (file) => {
    const idx = mediaFiles.findIndex(f => f.id === file.id);
    if (idx !== -1) {
      setLightboxIndex(idx);
    }
  };

  if (loading && !user) {
    return (
      <div className="ios-page flex items-center justify-center" data-testid="portal-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--ios-accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--ios-text-secondary)] font-semibold">Cargando portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ios-page" data-testid="portal-dashboard">
      {/* Admin Preview Banner */}
      {isAdminPreview && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 px-4 text-center text-sm font-medium">
          👁️ Vista de Admin — Estás viendo el portal como lo ve el cliente
          <button 
            onClick={() => window.close()} 
            className="ml-4 bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs"
          >
            Cerrar vista previa
          </button>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <MediaLightbox
          files={mediaFiles}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNext={() => setLightboxIndex(Math.min(lightboxIndex + 1, mediaFiles.length - 1))}
          onPrev={() => setLightboxIndex(Math.max(lightboxIndex - 1, 0))}
        />
      )}

      {/* Header - Liquid Glass */}
      <header className="ios-header !bg-[var(--ios-glass-sidebar)]">
        <div className="container mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            {/* Logo & Title - Responsive */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              {/* Logo */}
              {business?.logo_url ? (
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg overflow-hidden bg-[var(--ios-glass)] border border-[var(--ios-border-subtle)] shadow-sm flex-shrink-0">
                    <img 
                      src={business.logo_url} 
                      alt={business.name}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="hidden sm:block h-8 w-px bg-[var(--ios-border-subtle)]"></div>
                  <img 
                    src="/branding/logo-paliacate.png" 
                    alt="Paliacate Studio" 
                    className="hidden sm:block h-8 w-auto opacity-60"
                  />
                </div>
              ) : (
                <img 
                  src="/branding/logo-paliacate.png" 
                  alt="Paliacate Studio" 
                  className="h-10 sm:h-12 w-auto flex-shrink-0"
                />
              )}
              {/* Title - Truncate on mobile */}
              <div className="min-w-0">
                <h1 className="text-base sm:text-2xl font-bold text-gray-900 truncate">
                  {business?.name || 'Portal'}
                </h1>
                <p className="text-xs sm:text-sm text-[#6E6E73] truncate">
                  Hola, <strong>{user?.name?.split(' ')[0] || 'Cliente'}</strong>
                </p>
              </div>
              
              {/* Social Media Links - Only show if business has social media */}
              {business?.social_media && business.social_media.length > 0 && (
                <div className="hidden md:flex items-center pl-4 border-l border-gray-200">
                  <SocialMediaLinks socialMedia={business.social_media} compact={true} />
                </div>
              )}
            </div>
            
            {/* Actions - Hidden on mobile, use menu */}
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              {business && (
                <button
                  onClick={handleChangeBusiness}
                  className="hidden sm:block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all text-sm"
                  data-testid="change-business-btn"
                >
                  Cambiar
                </button>
              )}
              <a
                href="/"
                className="hidden sm:block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all text-sm"
              >
                Web
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 px-2 sm:px-4 py-2 text-gray-700 hover:text-[#e03c3c] hover:bg-gray-100 rounded-lg transition-all"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline text-sm">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation - Liquid Glass */}
      <div className="bg-[var(--ios-glass-sidebar)] border-b border-[var(--ios-border-subtle)] hidden md:block">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="ios-tab-bar inline-flex">
            <button
              onClick={() => setActiveTab('content')}
              className={`ios-tab flex items-center gap-2 ${
                activeTab === 'content' || activeTab === 'gallery' || activeTab === 'brand' ? 'active' : ''
              }`}
              data-testid="tab-content"
            >
              <Image size={18} />
              Mi Contenido
            </button>
            <button
              onClick={() => setActiveTab('deliverables')}
              className={`ios-tab flex items-center gap-2 ${activeTab === 'deliverables' ? 'active' : ''}`}
              data-testid="tab-deliverables"
            >
              <ImageIcon size={18} />
              Publicaciones
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`ios-tab flex items-center gap-2 ${
                activeTab === 'reports' || activeTab === 'progress' || activeTab === 'sessions' ? 'active' : ''
              }`}
              data-testid="tab-reports"
            >
              <BarChart3 size={18} />
              Reportes
            </button>
            <button
              onClick={() => setActiveTab('files')}
              className={`ios-tab flex items-center gap-2 ${activeTab === 'files' ? 'active' : ''}`}
              data-testid="tab-files"
            >
              <FolderOpen size={18} />
              Archivos
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`ios-tab flex items-center gap-2 ${
                activeTab === 'account' || activeTab === 'package' || activeTab === 'objectives' || activeTab === 'contracts' ? 'active' : ''
              }`}
              data-testid="tab-account"
            >
              <Wallet size={18} />
              Mi Cuenta
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Liquid Glass */}
      <nav className="ios-bottom-nav md:hidden" data-testid="client-bottom-nav">
        <button
          onClick={() => setActiveTab('content')}
          className={`ios-bottom-nav-item ${
            activeTab === 'content' || activeTab === 'gallery' || activeTab === 'brand' ? 'active' : ''
          }`}
        >
          <Image className="ios-bottom-nav-icon" />
          <span>Contenido</span>
        </button>
        <button
          onClick={() => setActiveTab('deliverables')}
          className={`ios-bottom-nav-item ${activeTab === 'deliverables' ? 'active' : ''}`}
        >
          <ImageIcon className="ios-bottom-nav-icon" />
          <span>Posts</span>
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`ios-bottom-nav-item ${
            activeTab === 'reports' || activeTab === 'progress' || activeTab === 'sessions' ? 'active' : ''
          }`}
        >
          <BarChart3 className="ios-bottom-nav-icon" />
          <span>Reportes</span>
        </button>
        <button
          onClick={() => setActiveTab('files')}
          className={`ios-bottom-nav-item ${activeTab === 'files' ? 'active' : ''}`}
        >
          <FolderOpen className="ios-bottom-nav-icon" />
          <span>Archivos</span>
        </button>
        <button
          onClick={() => setActiveTab('account')}
          className={`ios-bottom-nav-item ${
            activeTab === 'account' || activeTab === 'package' || activeTab === 'objectives' || activeTab === 'contracts' ? 'active' : ''
          }`}
        >
          <Wallet className="ios-bottom-nav-icon" />
          <span>Cuenta</span>
        </button>
      </nav>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        multiple
        className="hidden"
        data-testid="file-input"
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] flex items-center justify-center">
                <CloudUpload className="w-8 h-8 text-[var(--ios-text-primary)]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Subir Archivos</h3>
              <p className="text-[#6E6E73] mb-6">
                Tus archivos se guardaran en la carpeta <strong>"Compartido por Cliente"</strong>
              </p>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-4 px-6 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Upload size={20} />
                Seleccionar Archivos
              </button>
              
              <button
                onClick={() => setShowUploadModal(false)}
                className="mt-4 text-[var(--ios-text-tertiary)] hover:text-gray-700 text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uploading overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-12 h-12 border-4 border-[#e03c3c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-700 font-semibold">Subiendo archivos...</p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 pb-24 md:pb-8">
        {/* Social Media Links - Mobile Only (desktop shows in header) */}
        {business?.social_media && business.social_media.length > 0 && (
          <div className="md:hidden mb-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <SocialMediaLinks socialMedia={business.social_media} compact={false} />
            </div>
          </div>
        )}

        {/* Progress Tab Content */}
        {activeTab === 'progress' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ProgressTab />
          </div>
        )}

        {/* Reports Tab Content */}
        {activeTab === 'reports' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ClientReports 
              clientId={business?.client_id} 
              businessId={business?.business_id}
              clientData={business}
            />
          </div>
        )}

        {/* Sessions Tab Content */}
        {activeTab === 'sessions' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <UpcomingSessions />
          </div>
        )}

        {/* Content Review Tab - NEW */}
        {activeTab === 'content' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ContentReviewTab />
          </div>
        )}

        {/* Deliverables Tab Content (Historical) */}
        {activeTab === 'deliverables' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <DeliverablesTab />
          </div>
        )}

        {/* Approved Gallery Tab Content */}
        {activeTab === 'gallery' && (
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ApprovedGalleryTab clientId={user?.client_id} token={localStorage.getItem('portal_token')} />
          </div>
        )}

        {/* My Package Tab Content */}
        {activeTab === 'package' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ClientPackageSummary clientId={user?.client_id} />
          </div>
        )}

        {/* My Account Tab Content */}
        {activeTab === 'account' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <MyAccountTab />
          </div>
        )}

        {/* Objectives Tab Content */}
        {activeTab === 'objectives' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ObjectivesPortalTab />
          </div>
        )}

        {/* Brand Assets Tab Content */}
        {activeTab === 'brand' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <BrandAssetsTab />
          </div>
        )}

        {/* Contracts Tab Content */}
        {activeTab === 'contracts' && (
          <div className="bg-transparent rounded-xl sm:rounded-2xl p-3 sm:p-6 min-h-[400px] sm:min-h-[600px]">
            <ContractsPortalTab />
          </div>
        )}

        {/* Files Tab Content */}
        {activeTab === 'files' && (
          <>
        {/* Breadcrumb & Actions - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 text-sm overflow-x-auto scrollbar-hide">
            <button
              onClick={navigateToRoot}
              className="flex items-center gap-1 text-[#6E6E73] hover:text-[#e03c3c] transition-colors flex-shrink-0"
              data-testid="breadcrumb-root"
            >
              <Home size={16} />
              <span>Mi Carpeta</span>
            </button>
            
            {folderHistory.map((folder) => (
              <React.Fragment key={folder.id}>
                <ChevronRight size={16} className="text-[var(--ios-text-tertiary)] flex-shrink-0" />
                <span className="text-[var(--ios-text-tertiary)] truncate max-w-[100px]">{folder.name}</span>
              </React.Fragment>
            ))}
            
            {currentFolder && (
              <>
                <ChevronRight size={16} className="text-[var(--ios-text-tertiary)] flex-shrink-0" />
                <span className="text-gray-900 font-medium truncate max-w-[100px]">{currentFolder.name}</span>
              </>
            )}
          </div>

          {/* Actions: Upload + View toggle */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold"
              data-testid="upload-btn"
            >
              <Upload size={16} />
              <span>Subir</span>
            </button>
            
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('gallery')}
                className={`p-1.5 sm:p-2 rounded-md transition-all ${viewMode === 'gallery' ? 'bg-white shadow text-[#e03c3c]' : 'text-[var(--ios-text-tertiary)] hover:text-gray-700'}`}
                title="Vista galeria"
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow text-[#e03c3c]' : 'text-[var(--ios-text-tertiary)] hover:text-gray-700'}`}
                title="Vista lista"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="text-red-500" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-2">No se pueden cargar los archivos</h3>
                <p className="text-red-700 text-sm mb-4">{error}</p>
                
                {/* Specific guidance based on error type */}
                <div className="bg-white/50 rounded-lg p-4 text-sm">
                  {error.includes('no está conectado') || error.includes('not connected') ? (
                    <div className="space-y-2">
                      <p className="font-medium text-gray-800">¿Qué significa esto?</p>
                      <p className="text-[#6E6E73]">Tu carpeta de Google Drive aún no ha sido configurada por el equipo de Paliacate.</p>
                      <p className="text-[#6E6E73] font-medium mt-2">Próximo paso:</p>
                      <p className="text-[#6E6E73]">Contacta a tu ejecutivo de cuenta para que configure tu espacio de archivos.</p>
                    </div>
                  ) : error.includes('expiró') || error.includes('reconectar') ? (
                    <div className="space-y-2">
                      <p className="font-medium text-gray-800">¿Qué significa esto?</p>
                      <p className="text-[#6E6E73]">La conexión con Google Drive necesita actualizarse.</p>
                      <p className="text-[#6E6E73] font-medium mt-2">Próximo paso:</p>
                      <p className="text-[#6E6E73]">El administrador debe reconectar Google Drive para tu cuenta.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-medium text-gray-800">¿Qué puedo hacer?</p>
                      <ul className="list-disc list-inside text-[#6E6E73] space-y-1">
                        <li>Intenta recargar la página</li>
                        <li>Verifica tu conexión a internet</li>
                        <li>Si el problema persiste, contacta a soporte</li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => loadFolderContent(null, 'Mi Carpeta')}
                    className="px-4 py-2 bg-red-600 text-[var(--ios-text-primary)] rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw size={16} />
                    Reintentar
                  </button>
                  <a
                    href="https://wa.me/527774385617"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <MessageCircle size={16} />
                    Contactar Soporte
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-12 h-12 border-4 border-[#e03c3c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#6E6E73]">Cargando archivos...</p>
          </div>
        ) : (
          <>
            {/* Folders Section */}
            {folders.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Folder className="text-[#f47c2a]" size={20} />
                  Carpetas ({folders.length})
                </h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => navigateToFolder(folder.id, folder.name)}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-[#f47c2a] hover:shadow-lg transition-all text-left group"
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Folder className="text-[#f47c2a] group-hover:scale-110 transition-transform" size={36} />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{folder.name}</p>
                          <p className="text-xs text-[var(--ios-text-tertiary)]">{formatDate(folder.modifiedTime)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Media Gallery */}
            {mediaFiles.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Image className="text-blue-500" size={20} />
                    Fotos y Videos ({mediaFiles.length})
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLightboxIndex(0)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold"
                      data-testid="start-slideshow"
                    >
                      <Play size={16} />
                      Ver Presentación
                    </button>
                    <button
                      onClick={() => loadFolderContent(currentFolder?.id || null, currentFolder?.name || 'Mi Carpeta')}
                      className="flex items-center gap-2 text-sm text-[#6E6E73] hover:text-[#e03c3c] transition-colors"
                      data-testid="refresh-btn"
                    >
                      <RefreshCw size={16} />
                      Actualizar
                    </button>
                  </div>
                </div>

                {viewMode === 'gallery' ? (
                  /* Gallery Grid View */
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {mediaFiles.map((file, idx) => (
                      <div
                        key={file.id}
                        onClick={() => openLightbox(file)}
                        className="group relative aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:shadow-xl transition-all"
                        data-testid={`media-${file.id}`}
                      >
                        {file.thumbnailLink ? (
                          <img
                            src={file.thumbnailLink.replace('=s220', '=s400')}
                            alt={file.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {file.mimeType?.startsWith('video/') ? (
                              <Video size={40} className="text-purple-400" />
                            ) : (
                              <Image size={40} className="text-blue-400" />
                            )}
                          </div>
                        )}
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-[var(--ios-text-primary)] text-sm font-medium truncate">{file.name}</p>
                            <p className="text-[var(--ios-text-primary)]/70 text-xs">{formatFileSize(file.size)}</p>
                          </div>
                          <div className="absolute top-3 right-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full">
                              <Maximize2 size={16} className="text-[var(--ios-text-primary)]" />
                            </div>
                          </div>
                        </div>

                        {/* Video indicator */}
                        {file.mimeType?.startsWith('video/') && (
                          <div className="absolute top-3 left-3 p-2 bg-black/50 backdrop-blur-sm rounded-full">
                            <Play size={14} className="text-[var(--ios-text-primary)]" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* List View */
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="divide-y divide-gray-100">
                      {mediaFiles.map((file, idx) => (
                        <div
                          key={file.id}
                          className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4 cursor-pointer"
                          onClick={() => openLightbox(file)}
                          data-testid={`file-${file.id}`}
                        >
                          <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                            {file.thumbnailLink ? (
                              <img src={file.thumbnailLink} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {getFileIcon(file.mimeType)}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-grow min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{file.name}</p>
                            <div className="flex items-center gap-3 text-xs text-[var(--ios-text-tertiary)] mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDate(file.modifiedTime)}
                              </span>
                              {file.size && <span>{formatFileSize(file.size)}</span>}
                            </div>
                          </div>

                          <div className="flex-shrink-0">
                            <button className="p-2 text-[#e03c3c] hover:bg-[#e03c3c]/10 rounded-lg transition-colors">
                              <ZoomIn size={20} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Files */}
            {otherFiles.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <File className="text-[var(--ios-text-tertiary)]" size={20} />
                  Otros Archivos ({otherFiles.length})
                </h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="divide-y divide-gray-100">
                    {otherFiles.map((file) => (
                      <div
                        key={file.id}
                        className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                        data-testid={`file-${file.id}`}
                      >
                        <div className="flex-shrink-0">
                          {getFileIcon(file.mimeType)}
                        </div>
                        
                        <div className="flex-grow min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{file.name}</p>
                          <div className="flex items-center gap-3 text-xs text-[var(--ios-text-tertiary)] mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {formatDate(file.modifiedTime)}
                            </span>
                            {file.size && <span>{formatFileSize(file.size)}</span>}
                          </div>
                        </div>

                        <div className="flex-shrink-0 flex gap-2">
                          {file.webViewLink && (
                            <a
                              href={file.webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                            >
                              <Eye size={16} />
                              Ver
                            </a>
                          )}
                          {file.webContentLink && (
                            <a
                              href={file.webContentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-2 bg-[#e03c3c] text-[var(--ios-text-primary)] rounded-lg text-sm font-medium hover:bg-[#c83232] transition-colors"
                            >
                              <Download size={16} />
                              Descargar
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Empty state */}
            {files.length === 0 && folders.length === 0 && !error && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Folder className="w-20 h-20 text-[var(--ios-text-secondary)] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Carpeta vacía</h3>
                <p className="text-[#6E6E73]">No hay archivos ni carpetas aquí</p>
                {currentFolder && (
                  <button
                    onClick={navigateBack}
                    className="mt-4 text-[#e03c3c] hover:underline font-medium"
                  >
                    ← Volver atrás
                  </button>
                )}
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
};
